<template>
  <Dialog
    title="Custom Techpack"
    :showCloseButton="true"
    :showFooter="true"
    :showSaveButton="true"
    :showCancelButton="true"
    saveButtonText="Upload"
    cancelButtonText="Cancel"
    :sm="true"
    @closeEvent="closeEvent"
    @cancelEvent="closeEvent"
    @saveEvent="onSave"
    :loading="loading"
  >
    <template #modal-content>
      <!-- Upload Area -->
      <div
        class="border-2 border-dashed border-gray-400 rounded-lg p-6 text-center cursor-pointer hover:border-green-500 transition-all duration-200"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="handleDrop"
        @click="triggerFileSelect"
        :class="{ 'border-green-500 bg-green-50': isDragging }"
      >
        <p class="text-gray-600 font-medium">Drag & drop files (PDF, JPG, PNG)</p>
        <p class="text-sm text-gray-400 mt-1">or click to browse</p>
        <input
          type="file"
          ref="techpackFileRef"
          class="hidden"
          accept=".pdf,.jpg,.jpeg,.png"
          multiple
          @change="handleFileSelect"
        />
      </div>

      <!-- Selected Files (Before Upload) -->
      <div v-if="selectedFiles.length" class="mt-4 space-y-2">
        <div
          v-for="(file, index) in selectedFiles"
          :key="file.name + index"
          class="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded cursor-move"
          draggable="true"
          @dragstart="dragStart(index)"
          @dragover.prevent
          @drop="dropFile(index)"
        >
          <div class="flex items-center gap-2">
            <span class="drag-handle cursor-grab text-gray-500">☰</span>
            <span class="truncate max-w-xs">{{ file.name }}</span>
          </div>
          <button
            @click="removeSelectedFile(index)"
            class="text-red-600 border px-2 py-1 rounded hover:bg-red-50 transition"
          >
            Remove
          </button>
        </div>
      </div>

      <!-- Already Uploaded Files -->
      <div v-if="customFiles.length" class="mt-4 space-y-2">
        <div
          v-for="(file, index) in customFiles"
          :key="file.name + index"
          class="flex items-center justify-between p-3 border rounded hover:bg-gray-50 transition"
        >
          <span class="text-blue-600 truncate max-w-xs">{{ file.original_name }}</span>
          <div class="flex gap-2">
            <a
              :href="`${storage_url}/${file.path}`"
              target="_blank"
              class="text-green-600 border px-2 py-1 rounded hover:bg-green-50 transition"
            >
              Download
            </a>
            <button
              @click="deleteUploadedFile(file)"
              class="text-red-600 border px-2 py-1 rounded hover:bg-red-50 transition"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import Dialog from "@/components/Shared/Dialog.vue";
import { ref, onMounted, defineProps, defineEmits } from "vue";
import { useMainStore } from "@/store";
import { showConfirm, showToast } from "@/composables/swal";
import { handleResponseException } from "@/interceptors/axiosRequestInterceptor";

const props = defineProps<{ order: any }>();
const emits = defineEmits(["close", "onFetch"]);

const store = useMainStore();
const storage_url = import.meta.env.VITE_APP_FILE_URL;

const customFiles = ref<any[]>([]);
const selectedFiles = ref<File[]>([]);
const techpackFileRef = ref<HTMLInputElement | null>(null);
const loading = ref(false);
const isDragging = ref(false);

let dragIndex = ref<number | null>(null);

// ----------------------------
// Trigger file select
// ----------------------------
const triggerFileSelect = () => techpackFileRef.value?.click();

const handleFileSelect = () => {
  if (techpackFileRef.value?.files?.length) {
    selectedFiles.value.push(...Array.from(techpackFileRef.value.files));
  }
};

const handleDrop = (e: DragEvent) => {
  isDragging.value = false;
  if (e.dataTransfer?.files?.length) {
    selectedFiles.value.push(...Array.from(e.dataTransfer.files));
  }
};

// ----------------------------
// Drag & Drop reordering
// ----------------------------
const dragStart = (index: number) => {
  dragIndex.value = index;
};

const dropFile = (index: number) => {
  if (dragIndex.value === null) return;
  const dragged = selectedFiles.value[dragIndex.value];
  selectedFiles.value.splice(dragIndex.value, 1);
  selectedFiles.value.splice(index, 0, dragged);
  dragIndex.value = null;
};

// ----------------------------
// Remove selected file
// ----------------------------
const removeSelectedFile = (index: number) => selectedFiles.value.splice(index, 1);

// ----------------------------
// Load uploaded files
// ----------------------------
const loadFiles = async () => {
  try {
    const res = await store.fetchCustomTechpackFiles(props.order.id);
    const files = res.data.result.custom_techpack_pdf;
    customFiles.value = Array.isArray(files) ? files : files ? Object.values(files) : [];
  } catch (e) {
    handleResponseException(e);
  }
};

// ----------------------------
// Delete uploaded file
// ----------------------------
const deleteUploadedFile = async (file: any) => {
  const confirmed = await showConfirm("Are you sure?", "Delete this file?");
  if (!confirmed) return;

  try {
    await store.deleteSingleCustomTechpackFile(props.order.id, file.name);
    customFiles.value = customFiles.value.filter(f => f.name !== file.name);
    emits("onFetch", true);
    showToast("File deleted successfully", "success");
  } catch (e) {
    handleResponseException(e);
  }
};

// ----------------------------
// Upload files
// ----------------------------
const onSave = async () => {
  if (!selectedFiles.value.length) return showToast("Select files first", "error");

  loading.value = true;

  try {
    const formData = new FormData();
    selectedFiles.value.forEach(file => formData.append("files[]", file));

    await store.uploadMultipleCustomTechpackFiles(formData, props.order.id);

    selectedFiles.value = [];
    await loadFiles();
    emits("onFetch", true);
    emits("close");
  } catch (e) {
    handleResponseException(e);
  } finally {
    loading.value = false;
  }
};

// ----------------------------
// Close modal
// ----------------------------
const closeEvent = () => emits("close");

onMounted(loadFiles);
</script>
